/**
 * @fileoverview Inline "New message" composer for a job mailbox.
 *
 * Renders in the reading pane (like the draft reader) rather than a modal, so
 * composing a fresh email matches the rest of the mail surface. Human-authored
 * sends go straight out through `composeUserEmail` — the human is the approver,
 * so there is no draft queue here. The body is edited with the same rich-text
 * editor as drafts (bold / italic / lists / links); we persist both the HTML
 * part and a derived plain-text part.
 */

import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { AlertTriangle, Pencil, Send, Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Spinner } from "@/ui/primitives/spinner";
import { NotesEditor } from "@/ui/composed/editor/notes-editor";
import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { RecipientInput, type RecipientOption } from "./RecipientInput";
import { htmlToPlainText, plainTextToHtml } from "./htmlBody";
import {
  AttachmentChipList,
  MailAttachmentPicker,
} from "./MailAttachmentPicker";
import { ComposeSignaturePreview } from "./ComposeSignaturePreview";
import { useDraftAutosave } from "./useDraftAutosave";
import type { ComposerAttachment, UserDraftListItem } from "./types";

export function ComposeReader({
  jobId,
  mailboxEmail,
  onClose,
  onSent,
  onDraftWithMorgan,
  existingDraft,
  prefillTo,
  dismissAfterDraftWithMorgan,
}: {
  jobId: Id<"jobs">;
  mailboxEmail: string;
  /** Cancel — return to the empty reader state. */
  onClose: () => void;
  /** Fired after a successful send (defaults to `onClose`). */
  onSent?: () => void;
  /**
   * A user draft being resumed (MOR-783). The parent remounts this component
   * (keyed by draft id) when it changes, so seeding in useState is safe.
   */
  existingDraft?: UserDraftListItem | null;
  /**
   * Hand the current composer context (recipients / subject / rough body) to
   * the mail chat so Morgan can draft it. Receives a ready-made instruction
   * string that pre-fills the chat composer for the user to review and send.
   */
  onDraftWithMorgan?: (instruction: string) => void;
  /**
   * Recipients for a brand-new message opened against a known party (e.g.
   * from a contact record). Ignored when resuming an existing draft, which
   * carries its own addressees.
   */
  prefillTo?: string[];
  /**
   * Discard this composer's own draft and close once the brief has been
   * handed to Morgan. For surfaces where the hand-off IS the action (a
   * contact record): the rough notes have served their purpose, and keeping
   * them would leave a second, staler copy of the same email alongside
   * Morgan's. The mailbox leaves its composer open instead, so it does not
   * set this.
   */
  dismissAfterDraftWithMorgan?: boolean;
}) {
  const composeUserEmail = useMutation(api.mail.mutations.composeUserEmail);
  const jobContacts = useQuery(api.contacts.queries.listJobContacts, {
    jobId,
  });

  const recipientOptions: RecipientOption[] = useMemo(
    () =>
      (jobContacts ?? [])
        .map((c) => ({
          email: (c.effectiveEmail ?? c.email ?? "").trim(),
          displayName: c.displayName,
        }))
        .filter((c) => c.email.length > 0),
    [jobContacts],
  );

  const [to, setTo] = useState<string[]>(existingDraft?.to ?? prefillTo ?? []);
  const [cc, setCc] = useState<string[]>(existingDraft?.cc ?? []);
  const [bcc, setBcc] = useState<string[]>(existingDraft?.bcc ?? []);
  // Gmail-style: the Bcc row stays hidden until asked for. Populated values
  // always show — recipients about to receive the email are never invisible.
  const [bccOpen, setBccOpen] = useState(false);
  const showBcc = bccOpen || bcc.length > 0;
  const [subject, setSubject] = useState(existingDraft?.subject ?? "");
  // `bodyHtml` is the rich version the editor authors; `body` is the derived
  // plain text used for validation and the text/plain part.
  const [bodyHtml, setBodyHtml] = useState(() =>
    existingDraft
      ? (existingDraft.bodyHtml ?? plainTextToHtml(existingDraft.bodyText))
      : "",
  );
  const [body, setBody] = useState(existingDraft?.bodyText ?? "");
  const [attachments, setAttachments] = useState<ComposerAttachment[]>(
    (existingDraft?.attachments ?? []).map((a) => ({
      filename: a.filename,
      contentType: a.contentType,
      storageId: a.storageId,
      documentId: a.documentId,
    })),
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A missing subject is surfaced inline via the input placeholder (not the
  // bottom error banner), so the prompt sits where the user needs to act.
  const [subjectMissing, setSubjectMissing] = useState(false);
  const subjectRef = useRef<HTMLInputElement>(null);

  // Gmail-style autosave: the work-in-progress lands in the Drafts folder.
  const { flush, release, discard } = useDraftAutosave({
    jobId,
    enabled: !sending,
    initialDraftId: existingDraft?._id ?? null,
    payload: { to, cc, bcc, subject, bodyText: body, bodyHtml, attachments },
  });

  // A draft reopened after its send was rejected carries the error; the
  // row keeps its Failed status until Send succeeds.
  const failedError =
    existingDraft?.status === "failed" ? (existingDraft.error ?? null) : null;

  const handleBodyHtmlChange = (html: string) => {
    setBodyHtml(html);
    setBody(htmlToPlainText(html));
  };

  // Build a natural-language instruction from whatever the user has entered so
  // far and hand it to the mail chat. Only non-empty fields are included so
  // Morgan isn't fed blank headers.
  const handleDraftWithMorgan = () => {
    const toList = to.map((a) => a.trim()).filter(Boolean);
    const ccList = cc.map((a) => a.trim()).filter(Boolean);
    const bodyText = body.trim();

    // Recipients alone are not a brief. Without a subject or some notes
    // there is nothing to draft FROM, and Morgan would be guessing at what
    // the email is even about.
    if (subject.trim().length === 0 && bodyText.length === 0) {
      setError(
        "Add a subject, or a line or two on what you want said, so Morgan knows what to draft.",
      );
      return;
    }
    setError(null);

    const lines: string[] = ["Draft an email for me based on the following:"];
    if (toList.length > 0) lines.push(`To: ${toList.join(", ")}`);
    if (ccList.length > 0) lines.push(`Cc: ${ccList.join(", ")}`);
    if (subject.trim().length > 0) lines.push(`Subject: ${subject.trim()}`);
    if (bodyText.length > 0) {
      lines.push("", "Notes / rough content:", bodyText);
    }

    onDraftWithMorgan?.(lines.join("\n"));
    if (dismissAfterDraftWithMorgan) {
      void discard();
      onClose();
    }
  };

  const handleSend = async () => {
    setError(null);
    const toList = to.map((a) => a.trim()).filter(Boolean);
    const ccList = cc.map((a) => a.trim()).filter(Boolean);
    const bccList = bcc.map((a) => a.trim()).filter(Boolean);
    if (toList.length === 0) {
      setError("Add at least one recipient.");
      return;
    }
    if (subject.trim().length === 0) {
      // Prompt inline in the subject field rather than the bottom banner.
      setSubjectMissing(true);
      subjectRef.current?.focus();
      return;
    }
    if (body.trim().length === 0) {
      setError("Message body is required.");
      return;
    }

    setSending(true);
    try {
      // Persist the latest edits first so the send adopts THIS draft row:
      // a rejected send then reappears in Drafts as the same draft (with
      // the error) instead of vanishing.
      const draftId = await flush();
      await composeUserEmail({
        jobId,
        draftId: draftId ?? undefined,
        to: toList,
        cc: ccList.length > 0 ? ccList : undefined,
        bcc: bccList.length > 0 ? bccList : undefined,
        subject,
        bodyText: body,
        bodyHtml,
        attachments:
          attachments.length > 0
            ? attachments.map((a) => ({
                filename: a.filename,
                contentType: a.contentType,
                storageId: a.storageId,
                documentId: a.documentId,
              }))
            : undefined,
      });
      release();
      toast.success("Sending...");
      (onSent ?? onClose)();
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to send message"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header — mirrors the draft/thread reader header */}
      <div className="flex items-center justify-between gap-3 border-b px-6 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Pencil className="size-4" />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              New email
            </p>
            <p className="truncate text-xs text-muted-foreground">
              From {mailboxEmail}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={onClose}
            disabled={sending}
            aria-label="Close"
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pt-5">
        {failedError && (
          <div
            role="alert"
            className="mx-6 mb-4 flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3"
          >
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-destructive">
                This email couldn't be sent
              </p>
              <p className="mt-0.5 break-words text-xs leading-relaxed text-destructive/80">
                {failedError}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Check the recipients and attachments, then send again.
              </p>
            </div>
          </div>
        )}
        <div className="mx-6 mb-6 overflow-hidden rounded-xl border">
          <div className="flex items-start gap-2 border-b px-4 py-2">
            <span className="w-14 shrink-0 pt-1 text-xs font-medium text-muted-foreground">
              To:
            </span>
            <RecipientInput
              value={to}
              onChange={setTo}
              contacts={recipientOptions}
              placeholder="name@example.com"
            />
          </div>
          <div className="flex items-start gap-2 border-b px-4 py-2">
            <span className="w-14 shrink-0 pt-1 text-xs font-medium text-muted-foreground">
              Cc:
            </span>
            <RecipientInput
              value={cc}
              onChange={setCc}
              contacts={recipientOptions}
              placeholder="Optional"
            />
            {!showBcc && (
              <button
                type="button"
                onClick={() => setBccOpen(true)}
                className="shrink-0 pt-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                Bcc
              </button>
            )}
          </div>
          {showBcc && (
            <div className="flex items-start gap-2 border-b px-4 py-2">
              <span className="w-14 shrink-0 pt-1 text-xs font-medium text-muted-foreground">
                Bcc:
              </span>
              <RecipientInput
                value={bcc}
                onChange={setBcc}
                contacts={recipientOptions}
                placeholder="Recipients who won't see each other"
              />
            </div>
          )}
          <div className="flex items-center gap-2 border-b px-4 py-2">
            <span className="w-14 shrink-0 text-xs font-medium text-muted-foreground">
              Subject:
            </span>
            <input
              ref={subjectRef}
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                if (subjectMissing) setSubjectMissing(false);
              }}
              placeholder={
                subjectMissing ? "Subject is required" : "Add a subject"
              }
              className={cn(
                "min-h-7 flex-1 bg-transparent py-0.5 text-sm outline-none disabled:cursor-not-allowed",
                subjectMissing
                  ? "placeholder:text-destructive"
                  : "placeholder:text-muted-foreground/60",
              )}
            />
          </div>
          <NotesEditor
            value={bodyHtml}
            onChange={handleBodyHtmlChange}
            placeholder={
              onDraftWithMorgan
                ? "Write your email — or jot down what you want said and let Morgan draft it."
                : "Write your email…"
            }
            minHeightClass="min-h-[320px]"
            className="rounded-none border-0 bg-transparent"
          />
          <ComposeSignaturePreview jobId={jobId} />
          <AttachmentChipList
            jobId={jobId}
            attachments={attachments}
            onRemove={(index) =>
              setAttachments(attachments.filter((_, i) => i !== index))
            }
          />
          <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-3 py-2">
            <MailAttachmentPicker
              jobId={jobId}
              attachments={attachments}
              onChange={setAttachments}
              disabled={sending}
            />
            <Button
              size="sm"
              variant="ghost"
              className="mr-auto text-muted-foreground"
              onClick={() => {
                void discard().then(onClose);
              }}
              disabled={sending}
            >
              <Trash2 className="mr-1.5 size-3.5" />
              Discard
            </Button>
            {onDraftWithMorgan && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleDraftWithMorgan}
                disabled={sending}
              >
                <Sparkles className="mr-1.5 size-3.5" />
                Draft with Morgan
              </Button>
            )}
            <Button size="sm" onClick={handleSend} disabled={sending}>
              {sending ? (
                <Spinner className="mr-1.5 size-3.5" />
              ) : (
                <Send className="mr-1.5 size-3.5" />
              )}
              Send
            </Button>
          </div>
        </div>
        {error && (
          <p className="mx-6 -mt-3 mb-6 text-sm text-destructive">{error}</p>
        )}
      </div>
    </div>
  );
}
